import React, { useState, useEffect, forwardRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
    Dialog,
    Slide,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    TextField,
    MenuItem,
    Chip,
    Button,
    Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import Langs, { Localize } from "../../../app/lang/langs";
import {
    updateMedicalVisit,
    getAllMedicalVisits,
    upcomingVisitsStatuses,
    doneVisitsStatuses,
    medicalVisitsTypesOptions,
} from "../medicalVisistsSlice";
import moment from "moment";

const SlideUp = forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/* ── transition constant ── */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

/* ── glassmorphic field wrapper ── */
const glassFieldSx = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
        borderRadius: "14px",
        backgroundColor: "rgba(255,255,255,0.7)",
        backdropFilter: "blur(16px) saturate(1.4)",
        WebkitBackdropFilter: "blur(16px) saturate(1.4)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), 0 2px 8px rgba(0,0,0,0.03)",
        fontSize: "16px",
        transition: TRANSITION_SMOOTH,
        "& fieldset": {
            borderColor: "rgba(255,144,21,0.08)",
            transition: TRANSITION_SMOOTH,
        },
        "&:hover fieldset": {
            borderColor: "rgba(255,144,21,0.2)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "rgba(255,144,21,0.6)",
            boxShadow: "0 0 0 4px rgba(255,144,21,0.12), 0 0 20px rgba(255,144,21,0.06)",
        },
        "&.Mui-focused": {
            backgroundColor: "rgba(255,255,255,0.85)",
            boxShadow: "0 4px 16px rgba(255,144,21,0.08), inset 0 1px 0 rgba(255,255,255,0.9)",
        },
        "&.Mui-disabled": {
            backgroundColor: "rgba(0,0,0,0.03)",
            "& fieldset": { borderColor: "rgba(0,0,0,0.06)" },
        },
    },
    "& .MuiInputBase-input": {
        fontSize: "16px",
        color: "rgba(0,0,0,0.87)",
    },
    "& .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: "rgba(0,0,0,0.45)",
    },
    "& .MuiSelect-icon": {
        color: "rgba(0,0,0,0.4)",
        transition: TRANSITION_SMOOTH,
    },
    "& input::placeholder": {
        opacity: 0.45,
    },
};

const selectMenuProps = {
    sx: { zIndex: 1600 },
};

/* ── conclusion chip colors ── */
function conclusionChipProps(value) {
    switch (value) {
        case "Apte":
            return { color: "success", label: <Langs str="Apte" /> };
        case "Absent":
            return { color: "error", label: <Langs str="Absent" /> };
        case "Inapte":
            return { color: "default", label: <Langs str="Inapte" /> };
        default:
            return { color: "default", label: value || "—" };
    }
}

/* ── yes / no chip colors ── */
function yesNoChipProps(value) {
    if (value === "yes") return { color: "success", label: <Langs str="yes" /> };
    if (value === "no") return { color: "error", label: <Langs str="no" /> };
    return { color: "default", label: "—" };
}

/* ── format helpers ── */
function toDateTimeLocalStr(val) {
    if (!val) return "";
    return moment(val).format("YYYY-MM-DDTHH:mm");
}

function toDateStr(val) {
    if (!val) return "";
    return moment(val).format("YYYY-MM-DD");
}

function displayDateTime(val) {
    if (!val) return "—";
    return moment(val).format("DD/MM/YYYY HH:mm");
}

/* ════════════════════════════════════════════════════════════════════ */
export default function MobileViewMedicalVisitModal({ selectedMedicalVisit, onHide }) {
    const dispatch = useDispatch();
    const [updatedItem, setUpdatedItem] = useState(selectedMedicalVisit);

    useEffect(() => {
        if (selectedMedicalVisit) {
            setUpdatedItem({ ...selectedMedicalVisit });
        }
    }, [selectedMedicalVisit]);

    /* ── dirty check ── */
    const isDirty = useMemo(() => {
        if (!selectedMedicalVisit || !updatedItem) return false;
        return JSON.stringify(selectedMedicalVisit) !== JSON.stringify(updatedItem);
    }, [selectedMedicalVisit, updatedItem]);

    /* ── field change handler ── */
    const handleFieldChange = (value, key) => {
        setUpdatedItem((prev) => ({ ...prev, [key]: value }));
    };

    /* ── date formatter for save payload ── */
    function getFormatDate(dateKey) {
        if (updatedItem[dateKey]) {
            return moment(updatedItem[dateKey]).format("YYYY-MM-DD HH:mm:ss");
        }
        return null;
    }

    /* ── save ── */
    const handleSave = async () => {
        await dispatch(
            updateMedicalVisit({
                id: updatedItem["id"],
                status: updatedItem["status"],
                type: updatedItem["type"],
                type_follow_up: updatedItem["type_follow_up"],
                medical_center: updatedItem["medical_center"],
                comments: updatedItem["comments"],
                conclusion: updatedItem["conclusion"],
                theoretical_visit_date: getFormatDate("theoretical_visit_date"),
                next_visit_date: getFormatDate("next_visit_date"),
                planned_visit_date: getFormatDate("planned_visit_date"),
            })
        ).unwrap();
        onHide();
        dispatch(getAllMedicalVisits());
    };

    /* ── field definitions ── */
    const visibleFields = [
        { key: "status", type: "dropdown", options: upcomingVisitsStatuses.concat(doneVisitsStatuses) },
        { key: "type", type: "dropdown", options: medicalVisitsTypesOptions["type"] },
        { key: "type_follow_up", type: "dropdown", options: medicalVisitsTypesOptions["type_follow_up"] },
        { key: "team_name", type: "string", disabled: true },
        { key: "medical_center", type: "dropdown", options: medicalVisitsTypesOptions["medical_center"] },
        { key: "conclusion", type: "dropdown", options: medicalVisitsTypesOptions["conclusion"] },
        { key: "validation", type: "yes:no" },
        { key: "last_visit_date", type: "date:time", disabled: true },
        { key: "next_visit_date", type: "date:time" },
        { key: "theoretical_visit_date", type: "date:time" },
        { key: "planned_visit_date", type: "date:time" },
        { key: "sent_date", type: "date" },
        { key: "is_mid_career", type: "yes:no" },
        { key: "comments", type: "text" },
        { key: "created_at", type: "date:time", disabled: true },
        { key: "updated_at", type: "date:time", disabled: true },
    ];

    /* ── render a single field ── */
    const renderField = (field) => {
        const value = updatedItem ? updatedItem[field.key] : "";

        switch (field.type) {
            /* ── dropdown ── */
            case "dropdown": {
                if (field.key === "conclusion") {
                    return (
                        <TextField
                            select
                            fullWidth
                            size="small"
                            value={value || ""}
                            onChange={(e) => handleFieldChange(e.target.value, field.key)}
                            sx={glassFieldSx}
                            SelectProps={{
                                MenuProps: selectMenuProps,
                                renderValue: (sel) => (
                                    <Chip size="small" {...conclusionChipProps(sel)} />
                                ),
                            }}
                        >
                            {(field.options || []).map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                    <Chip size="small" {...conclusionChipProps(opt)} />
                                </MenuItem>
                            ))}
                        </TextField>
                    );
                }

                return (
                    <TextField
                        select
                        fullWidth
                        size="small"
                        value={value || ""}
                        onChange={(e) => handleFieldChange(e.target.value, field.key)}
                        sx={glassFieldSx}
                        SelectProps={{ MenuProps: selectMenuProps }}
                    >
                        {(field.options || []).map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {opt}
                            </MenuItem>
                        ))}
                    </TextField>
                );
            }

            /* ── disabled string ── */
            case "string":
                return (
                    <TextField
                        fullWidth
                        size="small"
                        disabled
                        value={value || ""}
                        sx={glassFieldSx}
                    />
                );

            /* ── date:time ── */
            case "date:time": {
                if (field.disabled) {
                    return (
                        <Box
                            sx={{
                                py: 1.2,
                                px: 2,
                                borderRadius: "14px",
                                background: "linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,248,240,0.4) 100%)",
                                backdropFilter: "blur(12px)",
                                border: "1px solid rgba(255,144,21,0.08)",
                                color: "rgba(0,0,0,0.5)",
                                fontSize: "15px",
                                minHeight: 44,
                                display: "flex",
                                alignItems: "center",
                                mb: 2,
                                transition: TRANSITION_SMOOTH,
                            }}
                        >
                            {displayDateTime(value)}
                        </Box>
                    );
                }

                return (
                    <TextField
                        fullWidth
                        size="small"
                        type="datetime-local"
                        value={toDateTimeLocalStr(value)}
                        onChange={(e) => handleFieldChange(e.target.value, field.key)}
                        sx={glassFieldSx}
                        InputLabelProps={{ shrink: true }}
                    />
                );
            }

            /* ── date (editable) ── */
            case "date":
                return (
                    <TextField
                        fullWidth
                        size="small"
                        type="date"
                        value={toDateStr(value)}
                        onChange={(e) => handleFieldChange(e.target.value, field.key)}
                        sx={glassFieldSx}
                        InputLabelProps={{ shrink: true }}
                    />
                );

            /* ── yes / no ── */
            case "yes:no":
                return (
                    <TextField
                        select
                        fullWidth
                        size="small"
                        value={value || ""}
                        onChange={(e) => handleFieldChange(e.target.value, field.key)}
                        sx={glassFieldSx}
                        SelectProps={{
                            MenuProps: selectMenuProps,
                            renderValue: (sel) => (
                                <Chip size="small" {...yesNoChipProps(sel)} />
                            ),
                        }}
                    >
                        <MenuItem value="yes">
                            <Chip size="small" color="success" label={<Langs str="yes" />} />
                        </MenuItem>
                        <MenuItem value="no">
                            <Chip size="small" color="error" label={<Langs str="no" />} />
                        </MenuItem>
                    </TextField>
                );

            /* ── multiline text ── */
            case "text":
                return (
                    <TextField
                        fullWidth
                        size="small"
                        multiline
                        minRows={3}
                        maxRows={8}
                        value={value || ""}
                        onChange={(e) => handleFieldChange(e.target.value, field.key)}
                        sx={glassFieldSx}
                    />
                );

            default:
                return (
                    <TextField
                        fullWidth
                        size="small"
                        value={value || ""}
                        disabled
                        sx={glassFieldSx}
                    />
                );
        }
    };

    /* ── header text ── */
    const headerText =
        updatedItem
            ? `${updatedItem["title"] || ""} ${updatedItem["prenom"] || ""} ${updatedItem["nom"] || ""}`.trim()
            : "";

    /* ════════════════════════════════════════════════════════════════ */
    return (
        <Dialog
            open={!!selectedMedicalVisit}
            onClose={onHide}
            fullScreen
            TransitionComponent={SlideUp}
            sx={{
                zIndex: 1400,
                "& .MuiDialog-paper": {
                    background: "linear-gradient(180deg, #f8f9fb 0%, #f0f1f5 100%)",
                },
            }}
        >
            {/* ── sticky app bar (glassmorphic over gradient) ── */}
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    backgroundColor: "rgba(255,255,255,0.82)",
                    color: "rgba(0,0,0,0.87)",
                    backdropFilter: "blur(24px) saturate(1.5)",
                    WebkitBackdropFilter: "blur(24px) saturate(1.5)",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    zIndex: 10,
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background: "linear-gradient(90deg, transparent, rgba(255,144,21,0.3), transparent)",
                    },
                }}
            >
                <Toolbar sx={{ minHeight: 56 }}>
                    <IconButton
                        edge="start"
                        onClick={onHide}
                        sx={{
                            minWidth: 44,
                            minHeight: 44,
                            borderRadius: "12px",
                            color: "rgba(0,0,0,0.7)",
                            transition: TRANSITION_SMOOTH,
                            "&:active": { transform: "scale(0.94)" },
                        }}
                        aria-label="Fermer"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 700,
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            letterSpacing: "-0.01em",
                            fontSize: "1.05rem",
                        }}
                    >
                        {headerText}
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* ── scrollable form body ── */}
            <Box
                sx={{
                    mt: 0,
                    px: 2,
                    pt: 2.5,
                    pb: isDirty ? "110px" : 3,
                    overflowY: "auto",
                    flex: 1,
                    scrollBehavior: "smooth",
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                        background: "rgba(255,144,21,0.2)",
                        borderRadius: 3,
                    },
                }}
            >
                {visibleFields.map((field, index) => (
                    <Fade in key={field.key} timeout={200 + index * 40}>
                        <Box sx={{ mb: 0.5 }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    mb: 0.75,
                                    fontWeight: 700,
                                    letterSpacing: "0.05em",
                                    color: "rgba(0,0,0,0.55)",
                                    fontSize: "0.73rem",
                                    textTransform: "uppercase",
                                    "&::before": {
                                        content: '""',
                                        display: "inline-block",
                                        width: 3,
                                        height: 14,
                                        borderRadius: 2,
                                        background: "linear-gradient(180deg, #FF9015, #FFC100)",
                                        flexShrink: 0,
                                    },
                                }}
                            >
                                <Langs str={field.key} />
                            </Typography>
                            {renderField(field)}
                        </Box>
                    </Fade>
                ))}
            </Box>

            {/* ── sticky bottom save button ── */}
            <Fade in={isDirty}>
                <Box
                    sx={{
                        position: "sticky",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        px: 2,
                        py: 2,
                        bgcolor: "rgba(255,255,255,0.88)",
                        backdropFilter: "blur(24px) saturate(1.5)",
                        WebkitBackdropFilter: "blur(24px) saturate(1.5)",
                        borderTop: "1px solid rgba(0,0,0,0.06)",
                        boxShadow: "0 -1px 12px rgba(0,0,0,0.06)",
                        zIndex: 10,
                    }}
                >
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        sx={{
                            borderRadius: "999px",
                            minHeight: 48,
                            fontWeight: 700,
                            textTransform: "none",
                            fontSize: "1rem",
                            background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                            "&:hover": { background: "linear-gradient(135deg, #FFC100 0%, #FF9015 100%)" },
                            "&:active": { transform: "scale(0.97)" },
                            boxShadow: "0 4px 16px rgba(255,144,21,0.35), 0 1px 3px rgba(255,144,21,0.2)",
                        }}
                    >
                        <Langs str="Save" />
                    </Button>
                </Box>
            </Fade>
        </Dialog>
    );
}
